import { type ReactNode, useEffect, useId, useRef } from 'react';
import type { UnitState } from '../booking/board';
import { useLocale } from '../i18n/LocaleProvider';

// Presentation components of the approved design system. They render what
// they are given; they hold no business rule and fetch nothing.

export const UNIT_STATE_LABEL: Readonly<Record<UnitState, Readonly<Record<'ar' | 'en', string>>>> = {
  available: { en: 'Available', ar: 'متاحة' },
  reserved: { en: 'Reserved', ar: 'محجوزة' },
  occupied: { en: 'Occupied', ar: 'مشغولة' },
  maintenance: { en: 'Maintenance', ar: 'صيانة' },
};

// Status is never colour alone: each state has its own glyph shape.
export function UnitGlyph({ state }: { readonly state: UnitState }) {
  return <i className={`er-glyph er-glyph--${state}`} aria-hidden="true" />;
}

export function UnitSquare({ state, large }: { readonly state: UnitState; readonly large?: boolean }) {
  const { locale } = useLocale();
  return (
    <span
      className={`er-usq er-usq--${state}${large ? ' er-usq--lg' : ''}`}
      role="img"
      aria-label={UNIT_STATE_LABEL[state][locale]}
    >
      <UnitGlyph state={state} />
    </span>
  );
}

export function UnitStatusChip({ state }: { readonly state: UnitState }) {
  const { locale } = useLocale();
  return (
    <span className={`er-ustat er-ustat--${state}`}>
      <UnitGlyph state={state} />
      {UNIT_STATE_LABEL[state][locale]}
    </span>
  );
}

export function StatusBar({ counts }: { readonly counts: Readonly<Record<UnitState, number>> }) {
  const { locale } = useLocale();
  const states: UnitState[] = ['available', 'reserved', 'occupied', 'maintenance'];
  const total = states.reduce((sum, state) => sum + counts[state], 0) || 1;
  return (
    <span
      className="er-statusbar"
      role="img"
      aria-label={states.map((state) => `${counts[state]} ${UNIT_STATE_LABEL[state][locale]}`).join(', ')}
    >
      {states.map((state) =>
        counts[state] ? (
          <i key={state} className={`is-${state}`} style={{ width: `${(counts[state] / total) * 100}%` }} />
        ) : null,
      )}
    </span>
  );
}

export type ChipTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'pending';

export function Chip({ tone = 'neutral', children }: { readonly tone?: ChipTone; readonly children: ReactNode }) {
  return <span className={tone === 'neutral' ? 'er-chip' : `er-chip er-chip--${tone}`}>{children}</span>;
}

export function Panel({
  title,
  actions,
  children,
  className,
  headingLevel = 2,
}: {
  readonly title?: ReactNode;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly headingLevel?: 2 | 3;
}) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className={className ? `er-panel ${className}` : 'er-panel'}>
      {title || actions ? (
        <div className="er-panel__head">
          {title ? <Heading className="er-panel__title">{title}</Heading> : null}
          {actions ? (
            <>
              <span className="er-spacer" />
              {actions}
            </>
          ) : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function PageHead({
  title,
  sub,
  actions,
}: {
  readonly title: ReactNode;
  readonly sub?: ReactNode;
  readonly actions?: ReactNode;
}) {
  return (
    <div className="er-pagehead">
      <div>
        <h1>{title}</h1>
        {sub ? <div className="er-pagesub">{sub}</div> : null}
      </div>
      {actions ? (
        <>
          <span className="er-spacer" />
          {actions}
        </>
      ) : null}
    </div>
  );
}

// Short business-date label ("03 Oct", or "03/10" in Arabic as in the
// prototype). Business dates are calendar dates, so no timezone shift.
export function shortDate(locale: 'ar' | 'en', iso: string | null | undefined): string {
  if (!iso) return '–';
  const [year, month, day] = iso.split('-');
  if (locale === 'ar') return `${day}/${month}`;
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day} ${names[Number(month) - 1] ?? month}${year && year !== String(new Date().getUTCFullYear()) ? ` ’${year.slice(2)}` : ''}`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word.replace(/^Al-/i, '').charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// A modal dialog (approved prototype er-modal). The dialog is a form, so its
// fields submit with Enter; Escape or the scrim closes it. Focus moves to the
// first field when it opens.
export function Modal({
  title,
  sub,
  onClose,
  onSubmit,
  children,
  footer,
  wide,
}: {
  readonly title: string;
  readonly sub?: ReactNode;
  readonly onClose: () => void;
  readonly onSubmit: () => void;
  readonly children: ReactNode;
  readonly footer: ReactNode;
  readonly wide?: boolean;
}) {
  const id = useId();
  const ref = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button[type="submit"]');
    first?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="er-overlay">
      <div className="er-scrim" aria-hidden="true" onClick={onClose} />
      <form
        ref={ref}
        className={wide ? 'er-modal er-modal--wide' : 'er-modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <div className="er-modal__head">
          <h2 className="er-modal__title" id={id}>
            {title}
          </h2>
          {sub ? <div className="er-cell-sub">{sub}</div> : null}
        </div>
        <div className="er-modal__body">{children}</div>
        <div className="er-modal__foot">{footer}</div>
      </form>
    </div>
  );
}
